import { Defaults, Routing } from "@common/_constants";
import { IconFrown } from "@common/components/Atoms/Icons";
import { GlobalConfig } from "@common/utils/config";
import * as React from "react";
import * as ReactDOMServer from "react-dom/server";
import * as DropzoneComponent from "react-dropzone-component";

// https://github.com/felixrieseberg/React-Dropzone-Component/
// http://www.dropzonejs.com/#configuration

interface FileDropProps {
    acceptedFiles?: string;
    dropMessage?: string;
    dataCy?: string;
    fileCategory: number;
    handleAdd?: (file_id: any) => void;
    handleRemove?: (file_id: any) => void;
    isMultiple: boolean;
    isValid: boolean;
    name: string;
    onFileError: (msg: string) => void;
    onFileUploaded: () => void;
    onFileUploading: () => void;
    validationMessages: string[];
}

interface FileDropState {
    tempFileList: any;
}
const defaultFileDropProps: FileDropProps = {
    fileCategory: Defaults.FileCategoryType.OriginalResume,
    isMultiple: false,
    isValid: true,
    name: "fileDrop",
    onFileError: () => {
        /**/
    },
    onFileUploaded: () => {
        /**/
    },
    onFileUploading: () => {
        /**/
    },
    validationMessages: [],
};

export class FileDrop extends React.Component<FileDropProps, FileDropState> {
    constructor(props: FileDropProps = defaultFileDropProps) {
        super(props);

        this.state = {
            tempFileList: {},
        };

        // tslint:disable-next-line:no-string-literal
        if (props.dropMessage) {
            this.dzJsConfig.dictDefaultMessage = props.dropMessage;
        }

        this.reset = this.reset.bind(this);
    }

    dzConfig: any = {
        postUrl: Routing.Api.General.FILE_POST + this.props.fileCategory,
        iconFiletypes: [".doc", ".docx", ".pdf"],
        showFiletypeIcon: false,
    };

    dzEvents = {
        error: this.handleError.bind(this),
        queuecomplete: this.handleQueueComplete.bind(this),
        sending: this.handleSending.bind(this),
        removedfile: this.handleRemove.bind(this),
        success: this.handleSuccess.bind(this),
        maxfilesexceeded: this.handleMaxFilesExceeded.bind(this),
        init: dz => (this.dzRef = dz),
    };

    dzRef: any;

    dzJsConfig = {
        acceptedFiles: this.props.acceptedFiles,
        multiple: this.props.isMultiple,
        maxFiles: this.props.isMultiple ? 10 : 1,
        headers: { Authorization: "Bearer " + GlobalConfig.ui.fileDropToken },
        addRemoveLinks: true,
        maxFilesize: 5, //5 Megs
        dictDefaultMessage: "Drop files here to upload",
        dictInvalidFileType: `Unsupported file type. Supported file types include: ${this.props.acceptedFiles}`,
        dictFileTooBig:
            "File is too big ({{filesize}}MB). Max filesize: {{maxFilesize}}MB.",
        previewTemplate: ReactDOMServer.renderToStaticMarkup(
            <div className="dz-preview dz-file-preview dz-processing dz-error dz-complete">
                <div className="dz-image">
                    <img data-dz-thumbnail="" />
                </div>
                <div className="dz-details">
                    <div className="dz-size">
                        <span data-dz-size="" />
                    </div>
                    <div className="dz-filename">
                        <span data-dz-name="" />
                    </div>
                </div>
                <div className="dz-progress">
                    <span className="dz-upload" data-dz-uploadprogress="" />
                </div>

                <div className="dz-success-mark">
                    <svg
                        width="54px"
                        height="54px"
                        viewBox="0 0 54 54"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <title>Check</title>
                        <defs />
                        <g
                            id="Page-1"
                            stroke="none"
                            strokeWidth="1"
                            fill="none"
                            fillRule="evenodd"
                        >
                            <path
                                d="M23.5,31.8431458 L17.5852419,25.9283877 C16.0248253,24.3679711 13.4910294,24.366835 11.9289322,25.9289322 C10.3700136,27.4878508 10.3665912,30.0234455 11.9283877,31.5852419 L20.4147581,40.0716123 C20.5133999,40.1702541 20.6159315,40.2626649 20.7218615,40.3488435 C22.2835669,41.8725651 24.794234,41.8626202 26.3461564,40.3106978 L43.3106978,23.3461564 C44.8771021,21.7797521 44.8758057,19.2483887 43.3137085,17.6862915 C41.7547899,16.1273729 39.2176035,16.1255422 37.6538436,17.6893022 L23.5,31.8431458 Z M27,53 C41.3594035,53 53,41.3594035 53,27 C53,12.6405965 41.3594035,1 27,1 C12.6405965,1 1,12.6405965 1,27 C1,41.3594035 12.6405965,53 27,53 Z"
                                id="Oval-2"
                                strokeOpacity="0.198794158"
                                stroke="#747474"
                                fillOpacity="0.816519475"
                                fill="#FFFFFF"
                            />
                        </g>
                    </svg>
                </div>
            </div>
        ),
    };

    reset() {
        this.setState({ tempFileList: {} }, () =>
            this.dzRef.removeAllFiles(true)
        );
    }

    handleSending() {
        this.props.onFileUploading();
    }

    handleQueueComplete() {
        this.props.onFileUploaded();
    }

    // https://stackoverflow.com/questions/22005054/dropzone-upload-only-one-file
    handleMaxFilesExceeded(file) {
        if (this.dzRef.options.maxFiles === 1) {
            this.dzRef.removeAllFiles();
            this.dzRef.addFile(file);
        } else {
            this.dzRef.removeFile(file);
        }
    }

    handleError(file, errorMessage) {
        const msg =
            typeof errorMessage === "string"
                ? errorMessage
                : (errorMessage &&
                      errorMessage.error_messages &&
                      errorMessage.error_messages[0]) ||
                  "an error occurred, please try again";

        this.dzRef.removeFile(file);
        this.props.onFileError(msg);
    }

    handleSuccess(file) {
        const response = JSON.parse(file.xhr.response);
        if (response.result.length > 0) {
            const file_id = response.result[0].file_id;
            this.setState(
                prevState => ({
                    tempFileList: {
                        ...prevState.tempFileList,
                        [file.name]: file_id,
                    },
                }),
                () => {
                    if (this.props.handleAdd) {
                        this.props.handleAdd(file_id);
                    }
                }
            );
        }
    }

    handleRemove(file) {
        let file_id = this.state.tempFileList[file.name];

        if (!file_id) {
            file_id = 0;
        }

        this.setState(
            prevState => ({
                tempFileList: { ...prevState.tempFileList, [file.name]: null },
            }),
            () => {
                if (this.props.handleRemove) {
                    this.props.handleRemove(file_id);
                }
            }
        );
    }

    render() {
        return (
            <div
                data-cy={this.props?.dataCy}
                className={
                    "c-control c-control--filedrop " +
                    (!this.props.isValid ? "has-error" : "")
                }
            >
                <DropzoneComponent.DropzoneComponent
                    config={this.dzConfig}
                    eventHandlers={this.dzEvents}
                    djsConfig={this.dzJsConfig}
                />

                {!this.props.isValid && (
                    <div className="c-control__message">
                        {this.props.validationMessages.map((msg, ind) => (
                            <div key={ind}>
                                <IconFrown />
                                <span>{msg + " "}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }
}

export default FileDrop;
